import { useState } from 'react';

function CodeBlock({ code, language = 'json5' }: { code: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group my-4">
      <div className="flex items-center justify-between bg-slate-800 rounded-t-lg px-4 py-2 border border-slate-700 border-b-0">
        <span className="text-xs text-slate-400 font-mono">{language}</span>
        <button onClick={handleCopy} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">
          <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
          {copied ? '已复制' : '复制'}
        </button>
      </div>
      <pre className="bg-slate-950 border border-slate-700 rounded-b-lg p-4 overflow-x-auto">
        <code className="text-sm text-slate-300 font-mono leading-relaxed">{code}</code>
      </pre>
    </div>
  );
}

function InfoBox({ type = 'info', children }: { type?: 'info' | 'warning' | 'tip'; children: React.ReactNode }) {
  const styles = {
    info: { bg: 'bg-cyan-500/5 border-cyan-500/20', icon: 'ri-information-line', iconColor: 'text-cyan-400' },
    warning: { bg: 'bg-amber-500/5 border-amber-500/20', icon: 'ri-alert-line', iconColor: 'text-amber-400' },
    tip: { bg: 'bg-emerald-500/5 border-emerald-500/20', icon: 'ri-lightbulb-line', iconColor: 'text-emerald-400' },
  };
  const s = styles[type];
  return (
    <div className={`${s.bg} border rounded-lg p-4 my-4`}>
      <div className="flex items-start gap-3">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className={`${s.icon} ${s.iconColor} text-lg`}></i>
        </div>
        <div className="text-sm text-slate-300 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function SectionTitle({ id, children }: { id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="text-xl font-bold text-white mt-10 mb-4 flex items-center gap-2 scroll-mt-24">
      <span className="w-1 h-5 bg-cyan-400 rounded-full inline-block"></span>
      {children}
    </h2>
  );
}

function SubTitle({ id, children }: { id?: string; children: React.ReactNode }) {
  return (
    <h3 id={id} className="text-base font-semibold text-slate-200 mt-6 mb-3 scroll-mt-24">{children}</h3>
  );
}

export default function BrowserContent() {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-3">浏览器工具</h1>
        <p className="text-slate-400 text-sm leading-relaxed">
          OpenClaw 内置浏览器工具，让智能体能够控制无头浏览器进行网页交互、截图、表单填写等操作。
          支持 openclaw 配置文件和 chrome 配置文件两种模式。
        </p>
      </div>

      <SectionTitle id="br-profiles">配置文件模式</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        浏览器工具支持两种配置文件模式，适用于不同场景：
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
        <div className="bg-slate-800/40 border border-cyan-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-global-line text-cyan-400"></i>
            <code className="text-cyan-400 font-mono text-sm font-bold">openclaw</code>
            <span className="text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-full">默认</span>
          </div>
          <p className="text-sm text-slate-300">使用 OpenClaw 管理的独立浏览器实例，完全隔离，适合自动化任务。每次启动都是干净的环境。</p>
        </div>
        <div className="bg-slate-800/40 border border-amber-500/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-chrome-line text-amber-400"></i>
            <code className="text-amber-400 font-mono text-sm font-bold">chrome</code>
          </div>
          <p className="text-sm text-slate-300">连接到用户本地已安装的 Chrome 浏览器，可以访问已登录的账号、Cookie 和扩展程序。</p>
        </div>
      </div>

      <SectionTitle id="br-config">配置示例</SectionTitle>
      <CodeBlock language="json5" code={`// openclaw.json
{
  agents: [
    {
      name: "web-agent",
      tools: ["browser", "group:web"],
      browser: {
        // 配置文件模式：openclaw（默认）或 chrome
        profile: "openclaw",
        // 是否显示浏览器窗口（调试时设为 true）
        headless: true,
        // 视口尺寸
        viewport: { width: 1280, height: 800 },
        // 用户代理字符串（可选）
        userAgent: "Mozilla/5.0 ...",
        // 超时时间（毫秒）
        timeout: 30000,
        // 是否启用 JavaScript
        javascript: true,
      }
    }
  ]
}`} />

      <SectionTitle id="br-multiprofile">多配置文件支持</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        可以为不同的智能体配置不同的浏览器配置文件，实现会话隔离：
      </p>
      <CodeBlock language="json5" code={`{
  agents: [
    {
      name: "agent-a",
      browser: {
        profile: "openclaw",
        // 指定独立的配置文件目录
        profileDir: "~/.openclaw/browser/agent-a",
      }
    },
    {
      name: "agent-b",
      browser: {
        profile: "chrome",
        // 使用 Chrome 的特定配置文件
        chromeProfile: "Profile 1",
      }
    }
  ]
}`} />
      <InfoBox type="tip">
        多个智能体使用不同的 <code className="text-emerald-300 bg-slate-800 px-1 py-0.5 rounded text-xs">profileDir</code> 可以实现完全的会话隔离，
        互不干扰，适合需要同时登录多个账号的场景。
      </InfoBox>

      <SectionTitle id="br-extension">Chrome 扩展中继</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        通过安装 OpenClaw Chrome 扩展，可以将本地 Chrome 浏览器作为中继，
        让智能体通过扩展控制浏览器，访问已登录的网站和本地扩展：
      </p>
      <ol className="space-y-3 text-sm text-slate-300 list-none mb-4">
        {[
          '从 Chrome 网上应用店安装 OpenClaw 扩展',
          '在扩展设置中配置 OpenClaw 服务器地址',
          '在 openclaw.json 中将 browser.profile 设为 "chrome"',
          '启动 OpenClaw，智能体即可通过扩展控制 Chrome',
        ].map((step, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="w-6 h-6 flex items-center justify-center bg-cyan-500/10 text-cyan-400 rounded-full text-xs font-bold flex-shrink-0 mt-0.5">{i + 1}</span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      <InfoBox type="warning">
        Chrome 扩展中继模式下，智能体可以访问您在 Chrome 中已登录的所有网站。
        请确保只在受信任的智能体上启用此功能。
      </InfoBox>

      <SectionTitle id="br-cli">CLI 快速参考</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        浏览器工具提供丰富的 CLI 命令，可以在智能体中直接调用：
      </p>
      <SubTitle id="br-cli-nav">导航与页面操作</SubTitle>
      <CodeBlock language="bash" code={`# 导航到指定 URL
browser navigate --url "https://example.com"

# 等待页面加载完成
browser wait --selector "#content" --timeout 5000

# 获取当前页面 URL
browser url

# 获取页面标题
browser title

# 获取页面完整 HTML
browser html

# 获取页面纯文本内容
browser text

# 截图（保存到文件）
browser screenshot --path "/tmp/screenshot.png"

# 截图（返回 base64）
browser screenshot --base64`} />

      <SubTitle id="br-cli-interact">交互操作</SubTitle>
      <CodeBlock language="bash" code={`# 点击元素
browser click --selector "#submit-btn"
browser click --text "登录"

# 输入文本
browser type --selector "#username" --text "admin"
browser type --selector "#password" --text "secret" --clear

# 选择下拉框
browser select --selector "#country" --value "CN"

# 勾选复选框
browser check --selector "#agree"

# 滚动页面
browser scroll --direction down --amount 500
browser scroll --selector "#footer"

# 悬停元素
browser hover --selector ".dropdown-trigger"

# 按键
browser key --key "Enter"
browser key --key "Tab"`} />

      <SubTitle id="br-cli-extract">数据提取</SubTitle>
      <CodeBlock language="bash" code={`# 获取元素文本
browser get-text --selector ".price"

# 获取元素属性
browser get-attr --selector "a.link" --attr "href"

# 获取所有匹配元素
browser query-all --selector ".item" --attr "text"

# 执行 JavaScript
browser eval --script "return document.title"
browser eval --script "window.scrollTo(0, document.body.scrollHeight)"`} />

      <SubTitle id="br-cli-wait">等待增强</SubTitle>
      <CodeBlock language="bash" code={`# 等待元素出现
browser wait --selector ".result" --timeout 10000

# 等待元素消失
browser wait --selector ".loading" --hidden --timeout 10000

# 等待网络空闲
browser wait --network-idle --timeout 15000

# 等待指定时间（毫秒）
browser wait --ms 2000

# 等待 URL 变化
browser wait --url-contains "/dashboard"`} />

      <SectionTitle id="br-snapshot">快照与 Ref</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        OpenClaw 浏览器工具支持两种快照类型，用于在不同时间点保存和恢复浏览器状态：
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-robot-line text-cyan-400"></i>
            <span className="text-sm font-semibold text-white">AI 快照</span>
          </div>
          <p className="text-sm text-slate-300 mb-2">由 AI 智能体自动创建，用于在工具调用之间保存浏览器状态。支持回滚到之前的状态。</p>
          <code className="text-xs text-cyan-400 font-mono">browser snapshot --name "before-login"</code>
        </div>
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <i className="ri-user-line text-amber-400"></i>
            <span className="text-sm font-semibold text-white">角色快照</span>
          </div>
          <p className="text-sm text-slate-300 mb-2">由用户手动创建，用于保存特定的登录状态或页面状态，可在多次会话中复用。</p>
          <code className="text-xs text-amber-400 font-mono">browser snapshot --role --name "logged-in"</code>
        </div>
      </div>
      <CodeBlock language="bash" code={`# 创建快照
browser snapshot --name "checkpoint-1"

# 列出所有快照
browser snapshot --list

# 恢复快照
browser snapshot --restore "checkpoint-1"

# 删除快照
browser snapshot --delete "checkpoint-1"`} />

      <SectionTitle id="br-debug">调试工作流</SectionTitle>
      <p className="text-slate-300 text-sm leading-relaxed mb-4">
        调试浏览器工具时，可以开启有头模式（headless: false）查看实际浏览器操作：
      </p>
      <CodeBlock language="json5" code={`// 调试配置
{
  agents: [
    {
      name: "debug-agent",
      browser: {
        headless: false,      // 显示浏览器窗口
        slowMo: 500,          // 每个操作延迟 500ms，便于观察
        devtools: true,       // 自动打开开发者工具
      }
    }
  ]
}`} />
      <InfoBox type="tip">
        调试完成后，记得将 <code className="text-emerald-300 bg-slate-800 px-1 py-0.5 rounded text-xs">headless</code> 改回 <code className="text-emerald-300 bg-slate-800 px-1 py-0.5 rounded text-xs">true</code>，
        以提高生产环境的性能。
      </InfoBox>
    </div>
  );
}
